// GitHub Trending 卡片渲染 - 从 githubTrendingHydrator 抽出以保持治理行数上限。
// spec §6.3: status 徽章 (Course Ready/In Progress/Adopted) + starsDelta 替换 starsToday。
import type { RepoRow } from "./githubTrendingHydratorTypes";
import type { GithubTrendingSegment } from "./githubTrendingHydratorTypes";
import { buildTopicCard } from "./topicCard";

const PERIOD_LABELS: Record<string, string> = {
	daily: "today",
	weekly: "this week",
	monthly: "this month",
};

export function buildCard(repo: RepoRow, displayRank?: number, segment?: GithubTrendingSegment): HTMLElement {
	// Completed 且有关联 topic -> Browse 风格 topic 卡片 (含功能性点赞/踩/收藏)
	if (repo.status === "completed" && repo.topicSlug) {
		return buildTopicCard({
			slug: repo.topicSlug,
			title: repo.topicTitle ?? repo.fullName,
			source: 'github_trending',
			subtitle: repo.topicSubtitle ?? "",
			category: repo.topicCategory,
			classification: repo.topicClassification,
			coverImageUrl: repo.topicCoverImageUrl ?? undefined,
			coverGradient: "grad-ai",
			chapterCount: repo.topicChapterCount ?? 0,
			openCount: repo.openCount ?? 0,
			favoriteCount: repo.favoriteCount ?? 0,
			voteValue: repo.voteValue ?? 0,
			isFavorite: repo.isFavorite ?? false,
			createdAt: repo.completedAt ?? repo.electedAt ?? new Date().toISOString(),
			isNewThisWeek: isNewThisWeek(repo.completedAt ?? repo.electedAt ?? new Date().toISOString()),
		}, {
			showSubtitle: true,
			variant: 'mobile-compact',
		});
	}

	const card = document.createElement("a");
	card.className = "gh-repo-card gh-repo-card--source-reference";
	const sourceHref = githubRepositoryHref(repo);
	if (sourceHref) card.href = sourceHref;
	card.setAttribute('aria-disabled', 'true');
	card.tabIndex = -1;
	// Keep the public source href in the rendered DOM for crawlers without
	// letting a card click take a learner away from DeepMox.
	card.addEventListener('click', (event) => event.preventDefault());
	card.dataset.searchText = [
		repo.fullName,
		repo.description,
		repo.language,
	].filter(Boolean).join(" ").toLowerCase();

	// rank 段: 用 rank 设置 data-period (供前端筛选); repo 段 rank=null -> "all"
	if (repo.rank != null && repo.rank <= 3) {
		card.setAttribute("data-period", "today");
	} else if (repo.rank != null) {
		card.setAttribute("data-period", "week");
	} else {
		card.setAttribute("data-period", "all");
	}

	// In Progress 卡片应与 Today/This Week/This Month 卡片完全一致:
	// 不显示 topic 章节信息、状态徽章、Deep Dive 链接。
	// 用户已通过 In Progress 胶囊筛选进入,无需重复展示状态。
	const isInProgress = repo.status === "queued" || repo.status === "processing";

	const rank = document.createElement("div");
	// 序号逻辑: 优先用 displayRank (列表位置 1,2,3...), 其次用 repo.rank (DB 中的 GitHub 原始排名)
	const effectiveRank = displayRank ?? repo.rank;
	const rankNumber = Number.isFinite(effectiveRank ?? NaN) && (effectiveRank ?? 0) > 0 ? effectiveRank! : 0;
	rank.className = `gh-repo-rank${rankNumber > 0 && rankNumber <= 3 ? " top-3" : ""}`;
	rank.textContent = rankNumber > 0 ? String(rankNumber) : "·";
	card.appendChild(rank);

	const info = document.createElement("div");
	info.className = "gh-repo-info";
	const name = document.createElement("div");
	name.className = "gh-repo-name";
	name.appendChild(document.createTextNode(repo.fullName || "unknown"));
	info.appendChild(name);
	if (repo.description) {
		const desc = document.createElement("p");
		desc.className = "gh-repo-desc";
		desc.textContent = repo.description;
		info.appendChild(desc);
	}
	const meta = document.createElement("div");
	meta.className = "gh-repo-meta";
	if (repo.language) {
		const lang = document.createElement("span");
		lang.appendChild(document.createTextNode(repo.language));
		meta.appendChild(lang);
	}
	const forks = document.createElement("span");
	forks.textContent = `Fork ${formatNum(repo.forks)}`;
	meta.appendChild(forks);
	if (!isInProgress && repo.topicChapterCount != null) {
		const topic = document.createElement("span");
		topic.textContent = `Topic: ${repo.topicChapterCount} chapters`;
		meta.appendChild(topic);
	}
	// 状态徽章: completed -> "Course Ready"; adopted 无 topic -> "Adopted"
	// In Progress 卡片不显示徽章(用户已通过胶囊筛选进入此段)
	if (!isInProgress) {
		const statusBadge = buildStatusBadge(repo);
		if (statusBadge) meta.appendChild(statusBadge);
	}
	info.appendChild(meta);

	if (!isInProgress && repo.topicSlug) {
		const deep = document.createElement("div");
		deep.className = "gh-deep-link";
		deep.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path></svg> Deep Dive Topic';
		info.appendChild(deep);
	}

	card.appendChild(info);

	const stars = document.createElement("div");
	stars.className = "gh-repo-stars";
	const sc = document.createElement("span");
	sc.className = "star-count";
	sc.textContent = formatNum(repo.stars);
	stars.appendChild(sc);
	const sl = document.createElement("span");
	sl.className = "star-label";
	sl.textContent = "stars";
	stars.appendChild(sl);
	// starsDelta: rank 段有值, repo 段 null
	// 标签随段变化: daily -> "today", weekly -> "this week", monthly -> "this month"
	if (repo.starsDelta != null) {
		const st = document.createElement("span");
		st.className = "star-today";
		const periodLabel = (segment && PERIOD_LABELS[segment]) ? PERIOD_LABELS[segment] : "today";
		st.textContent = `+${repo.starsDelta} ${periodLabel}`;
		stars.appendChild(st);
	}
	card.appendChild(stars);

	return card;
}

function githubRepositoryHref(repo: RepoRow): string | null {
	const candidate = repo.repoUrl || `https://github.com/${repo.fullName}`;
	try {
		const url = new URL(candidate);
		if (url.protocol !== 'https:' || url.hostname !== 'github.com' || url.username || url.password) return null;
		const [owner, name, ...rest] = url.pathname.split('/').filter(Boolean);
		if (rest.length > 0 || !owner || !name || !/^[A-Za-z0-9_.-]+$/.test(owner) || !/^[A-Za-z0-9_.-]+$/.test(name)) return null;
		return `https://github.com/${owner}/${name}`;
	} catch {
		return null;
	}
}

function buildStatusBadge(repo: RepoRow): HTMLElement | null {
	if (repo.status === "completed" && repo.topicSlug) {
		const badge = document.createElement("span");
		badge.className = "pill";
		badge.style.cssText = "background:var(--success-bg);color:var(--success);font-size:11px;padding:2px 8px;";
		badge.textContent = "Course Ready";
		return badge;
	}
	if (repo.status === "processing" || repo.status === "queued") {
		const badge = document.createElement("span");
		badge.className = "pill";
		badge.style.cssText = "background:var(--warning-bg);color:var(--warning);font-size:11px;padding:2px 8px;";
		badge.textContent = "In Progress";
		return badge;
	}
	if (repo.isAdopted) {
		const badge = document.createElement("span");
		badge.className = "pill";
		badge.style.cssText = "background:var(--info-bg);color:var(--info);font-size:11px;padding:2px 8px;";
		badge.textContent = "Adopted";
		return badge;
	}
	return null;
}

function formatNum(n: number): string {
	if (n >= 1000) {
		return n >= 10000 ? `${(n / 1000).toFixed(1)}k` : `${(n / 1000).toFixed(2)}k`.replace(/\.?0+$/, "");
	}
	return String(n);
}

function isNewThisWeek(iso: string): boolean {
	return Date.now() - new Date(iso).getTime() < 7 * 86400000;
}

export function buildEmpty(message: string): HTMLElement {
	const div = document.createElement("div");
	div.style.cssText = "text-align:center;padding:48px 20px;color:var(--fg-tertiary);grid-column:1 / -1;";
	div.innerHTML = `<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="margin-bottom:12px;opacity:0.3;"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg><p style="font-size:14px;font-weight:500;">${message}</p>`;
	return div;
}

export function renderSkeleton(skeletonClassName = 'gh-repo-skeleton'): string {
	return Array.from({ length: 5 }, () => `<div class="${skeletonClassName}"></div>`).join('');
}
